import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { executeCommerceToolsWithRetry } from "@lib/utils/executeCommerceToolsWithRetry";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import { useUpdateCart } from "@lib/commerce/hooks/checkoutOperations";
import { useLock } from "@store/hooks";
import { buildSupplyChannelActions } from "@lib/utils/checkout-utility/supplyChannelActions";
import { Button } from "@ecco/ui/button";
import { Circle } from "@ecco/ui/circle";
import { Flex } from "@ecco/ui/flex";
import { use, useState } from "react";
import type { StoreState } from "@store/types";
import { useStore, useStoreApi } from "@store/index";
import type { StoreApi } from "zustand";
import { Controller } from "../store-locator/controller";
import type { StoreData } from "@features/stores/types";
import ShippingMethods from "@lib/constants/contentful/shipping-methods";
import { useLocale } from "next-intl";
import { useCheckout } from "@lib/utils/checkout-utility/checkoutUtility";
import { ShippingMethodTypes } from "@features/checkout/types";
import { getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";
import {
    mapClickAndCollectStoreToDeliveryAddress,
    mapParcelShopToDeliveryAddress,
} from "@features/checkout/components/delivery-step/utils/mapPickupPointToAddress";
import { hasGraphQLErrors } from "@lib/utils/graphql/hasGraphQLErrors";
import type { GraphQLError } from "graphql";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

type Props = {
    store?: StoreData;
    onClose?: () => void;
    hideParcelShopAvailabilityComponent: boolean;
};

export const AvailabilityComponentCheckout = ({
    store,
    onClose,
    hideParcelShopAvailabilityComponent = false,
}: Props) => {
    const { onStoreSelected, showStoreBadge, shippingMethodKey } = use(Controller);
    const setStoreData = useStore((state) => state.setStoreData);
    const setParcelShopData = useStore((state) => state.setParcelShopData);

    const lock = useLock();
    const api: StoreApi<StoreState> = useStoreApi();

    const errorToaster = useErrorToast();
    const locale = useLocale();
    const { get: getMicrocopy, getMultiple } = useMicrocopy();
    const executeUpdateCart = useUpdateCart();
    const lineItems = api?.getState().data?.me?.cartInfo?.cart?.lineItems;
    const hasLineItems = (lineItems?.length ?? 0) > 0;
    const [loading, setLoading] = useState(false);

    const { addDeliveryMethod, addDeliveryAddress } = useCheckout();

    const updateDeliveryMethod = async () => {
        if (!store) {
            return {};
        }

        if (shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP) {
            const deliveryAddress = mapParcelShopToDeliveryAddress(store);
            const { errors: deliveryAddressErrors } = await addDeliveryAddress(deliveryAddress);
            const { errors: deliveryMethodErrors } = await addDeliveryMethod(
                ShippingMethodTypes.PARCEL_SHOP
            );
            setStoreData(undefined);
            return {
                deliveryAddressErrors,
                deliveryMethodErrors,
            };
        }
        if (shippingMethodKey === ShippingMethodTypes.CLICK_AND_COLLECT) {
            const deliveryAddress = mapClickAndCollectStoreToDeliveryAddress(store);
            const { errors: deliveryAddressErrors } = await addDeliveryAddress(deliveryAddress);
            const { errors: deliveryMethodErrors } = await addDeliveryMethod(
                getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT)
            );
            setParcelShopData(undefined);
            return {
                deliveryAddressErrors,
                deliveryMethodErrors,
            };
        }

        return {};
    };

    const handleSetStore = lock(async () => {
        if (!hasLineItems) {
            return;
        }

        setLoading(true);
        // Read the current cart so the write uses the latest version; the shared retry
        // util recovers from any genuine ConcurrentModification.
        const cart = api.getState().data.me.cartInfo.cart;
        let addressErrors: GraphQLError[] = [];
        let methodErrors: GraphQLError[] = [];
        const supplyChannelId = store.supplyChannel?.[0]?.id;
        Logger.info(ServiceType.WEB, "Setting pickup store", {
            tag: LogTag.SHIPPING,
            storeId: store.storeId,
            supplyChannelId,
            deliveryMethod: shippingMethodKey,
            cartId: cart.id,
        });
        const { errors: supplyChannelErrors } = await executeCommerceToolsWithRetry(
            executeUpdateCart,
            {
                cartId: cart.id,
                cartVersion: cart.version,
                storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
                locale: locale,
                actions: buildSupplyChannelActions(cart.lineItems, {
                    id: supplyChannelId,
                }),
            },
            0,
            "Setting store supply channel on all line items"
        );
        if (shippingMethodKey) {
            const { deliveryAddressErrors, deliveryMethodErrors } = await updateDeliveryMethod();
            addressErrors = deliveryAddressErrors || [];
            methodErrors = deliveryMethodErrors || [];
        }
        if (hasGraphQLErrors(supplyChannelErrors, addressErrors, methodErrors)) {
            Logger.error(ServiceType.WEB, "Error adding delivery method", {
                tag: LogTag.SHIPPING,
                supplyChannelErrors,
                addressErrors,
                methodErrors,
                store,
            });
            errorToaster({
                title: microcopies[CommonCMS.error.general.title],
            });
        }
        onStoreSelected(store);
        setLoading(false);
        onClose();
    });

    const allItemsAvailableInStore = lineItems?.every((lineItem) => {
        return lineItem?.variant?.availability?.channels?.results?.some((channel) => {
            return (
                channel.channel.id === store?.supplyChannel?.[0].id &&
                channel.availability.isOnStock
            );
        });
    });

    const unavailableItems = lineItems?.filter((lineItem) => {
        return !lineItem?.variant?.availability?.channels?.results?.some((channel) => {
            return (
                channel.channel.id === store?.supplyChannel?.[0].id &&
                channel.availability.isOnStock
            );
        });
    });

    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            ClickAndCollect.availableSoon,
            ClickAndCollect.available,
            ClickAndCollect.selectStore,
            ClickAndCollect.changeStore,
            ClickAndCollect.availableSoon,
            ShippingMethods.parcelShops.select,
        ]);
    })();

    const unavailableInStoresText = getMicrocopy(
        CommonCMS.page,
        unavailableItems?.length === 1
            ? ClickAndCollect.availableSoon
            : ClickAndCollect.availableItems,
        {
            replacements: {
                storesWithoutStock: unavailableItems?.length,
            },
        }
    );
    const availabilityColor = allItemsAvailableInStore ? "green.200" : "yellow.200";
    let availabilityText: string;
    if (allItemsAvailableInStore) {
        availabilityText = microcopies[ClickAndCollect.available];
    } else {
        availabilityText = unavailableInStoresText;
    }

    return (
        <Flex
            justifyContent={"space-between"}
            color="black"
            fontWeight="bold"
            flexDirection={["column", "column", "row"]}
            gap={4}
        >
            <Flex gap={2} fontSize={"mobileBodyTextTiny"} alignItems={"center"}>
                {!hideParcelShopAvailabilityComponent && (
                    <>
                        <Circle size={2} bg={availabilityColor} />
                        {availabilityText}
                    </>
                )}
            </Flex>
            <Button
                type="button"
                zIndex={10}
                h={10}
                w={["100%", "100%", "auto"]}
                fontSize="mobileBodyTextNormal"
                onClick={hasLineItems ? handleSetStore : undefined}
                isLoading={loading}
                tabIndex={hasLineItems ? undefined : -1}
                aria-disabled={!hasLineItems || undefined}
                pointerEvents={hasLineItems ? undefined : "none"}
            >
                <span>
                    {showStoreBadge
                        ? microcopies[ClickAndCollect.selectStore]
                        : microcopies[ShippingMethods.parcelShops.select]}
                </span>
            </Button>
        </Flex>
    );
};
